import { sortStripeProductsByOrderNumber } from '@/common/stripe';
import { formatStripePrice, getNumberOrUndefined, isDefined, safeSort } from '@/common/utils';
import { CompactButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { useLayoutContext, useShoppingCart } from '@/contexts';
import { ContentWithLoad } from '@/layouts/content-with-load';
import { PrimaryLayout } from '@/layouts/primary';
import { theme } from '@/theme';
import { Badge, Card, Flex, Grid, Image, NumberInput, Popover, Text } from '@mantine/core';
import { IconHelpCircle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import {
	GetStripeOneTimeProductsQuery,
	useGetStripeOneTimeProductsQuery,
} from './graphql.generated';
import placeHolderSvg from './placeholder.svg';
import styles from './styles.module.css';

export const ShopPage = () => {
	const navigate = useNavigate();
	const { loading, data, error } = useGetStripeOneTimeProductsQuery();
	const { totalItemsInCart } = useShoppingCart();
	const { isDesktop } = useLayoutContext();
	const checkoutButton = useMemo(
		() => (
			<StandardButton
				onClick={() => navigate({ to: '/checkout-product' })}
				rightSection={
					totalItemsInCart > 0 && (
						<Badge circle color={theme.colors?.blue?.[9]}>
							{totalItemsInCart}
						</Badge>
					)
				}
			>
				Checkout
			</StandardButton>
		),
		[navigate, totalItemsInCart]
	);

	const products = useMemo(
		() => safeSort(data?.stripeOneTimeProducts, sortStripeProductsByOrderNumber),
		[data?.stripeOneTimeProducts]
	);

	const helpButton = useMemo(
		() => (
			<CompactButton leftSection={<IconHelpCircle />} onClick={() => navigate({ to: '/help' })}>
				Help
			</CompactButton>
		),
		[navigate]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Shop for bags', children: helpButton }}
			footer={{ children: checkoutButton }}
			context="home"
		>
			<ContentPanel>
				<CustomContentSection
					location={ContentSectionLocation.Shop}
					placement={ContentSectionPlacement.Top}
				/>
				{!isDesktop && (
					<ContentWithLoad
						loading={loading}
						error={!!error}
						isEmpty={!products || products.length === 0}
						isEmptyMessage="No products available"
						isErrorMessage="An Error occurred"
					>
						<Grid gutter="lg" justify="center" align="stretch">
							{products?.map((product) => (
								<Grid.Col key={product?.id} span={6}>
									<Product product={product} />
								</Grid.Col>
							))}
						</Grid>
					</ContentWithLoad>
				)}
				{isDesktop && (
					<Grid pt={100} w="100%" gutter="lg">
						{products?.map((product) => (
							<Grid.Col key={product?.id} span={4}>
								<Product product={product} />
							</Grid.Col>
						))}
					</Grid>
				)}
				<CustomContentSection
					location={ContentSectionLocation.Shop}
					placement={ContentSectionPlacement.Middle}
				/>
				<CustomContentSection
					location={ContentSectionLocation.Shop}
					placement={ContentSectionPlacement.Bottom}
				/>
			</ContentPanel>
			{isDesktop && (
				<Flex
					justify="center"
					bg="white"
					align="center"
					w="100%"
					p="sm"
					className={styles.fixedBottom}
				>
					<Flex w={200} direction="row" gap="sm">
						{checkoutButton}
					</Flex>
				</Flex>
			)}
		</PrimaryLayout>
	);
};

type Product = NonNullable<GetStripeOneTimeProductsQuery['stripeOneTimeProducts']>[number];

const Product = ({ product }: { product: Product }) => {
	const { quantityPerPriceItemId, setQuantity } = useShoppingCart();
	const { isDesktop } = useLayoutContext();
	const quantity = quantityPerPriceItemId.get(product?.default_price?.id ?? '') ?? 0;
	const freeTagLimit = getNumberOrUndefined(product?.metadata?.free_tag_limit);

	const handleQuantityChange = useCallback(
		(value: number | string) => {
			if (typeof value === 'string' || !product?.default_price?.id) {
				return;
			}
			setQuantity(product?.default_price.id, value);
		},
		[setQuantity, product?.default_price?.id]
	);

	const info =
		isDefined(freeTagLimit) && quantity > freeTagLimit
			? `This product has a limit of ${freeTagLimit} units.`
			: undefined;

	return (
		<Card bg="green.0" h="100%">
			<Card.Section>
				<Image
					src={product?.images?.[0] ?? { placeHolderSvg }}
					fallbackSrc={placeHolderSvg}
					alt={`Image for product: ${product?.name}`}
					height={!isDesktop ? 150 : 200}
					fit="contain"
					m="auto"
				/>
			</Card.Section>
			<Card.Section>
				<Flex direction="column" align="center" gap={2}>
					<Text fw={700} size="md" c="blue.9">
						{product?.name}
					</Text>
					<Text fw={500} size="sm" c="blue.9">
						{product?.description}
					</Text>
				</Flex>
				<Flex direction="column" align="center" m="20px 0">
					<Text fw="700" c="blue.9">
						{formatStripePrice(product?.default_price?.unit_amount)}
					</Text>
				</Flex>
			</Card.Section>
			{quantity === 0 ? (
				<CompactButton
					variant="white"
					color="green.1"
					mt="auto"
					size="lg"
					onClick={() => setQuantity(product?.default_price?.id, quantity + 1)}
				>
					Add to cart
				</CompactButton>
			) : (
				<Popover position="bottom" withArrow opened={!!info}>
					<Popover.Target>
						<NumberInput
							mt="auto"
							size="lg"
							value={quantity}
							{...(isDefined(freeTagLimit) && { max: freeTagLimit })}
							onChange={handleQuantityChange}
							allowNegative={false}
						/>
					</Popover.Target>
					<Popover.Dropdown>{info && <Text size="xs">{info}</Text>}</Popover.Dropdown>
				</Popover>
			)}
		</Card>
	);
};
